// @ts-nocheck
import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import config from 'config';

import HomeListRows from 'components/common/HomeListRows';
import SettingsRow from 'components/pages/home/SettingsRow';
import RectangleRow from 'components/pages/home/RectangleRow';
import CircleRow from 'components/pages/home/CircleRow';

import Ref from 'models/propTypes/ref';
import focusManager from 'support/focus/FocusManager';
import { isPromoRow, isRowWithScoring } from 'support/rowHelper';
import { getHighlightsRowData } from 'support/scoringHelper';
import { useIntl } from 'react-intl';

import { useScoring } from 'hooks/scoring';
import HomeRow from './HomeRow';

import styles from './HomeRows.module.scss';

const LEAGUE_ROW_SLUG = 'leagues-button-row';
const SPORTS_ROW_SLUG = 'sports-button-row';
const LOCAL_MARKETS_ROW_SLUG = 'markets-button-row';
const SHOWS_ROW_SLUG = 'shows-button-row';

const brand = config.buildBrandId;

const messages = {
  ttsEndOfNavigation: {
    id: 'navigation.tts.ttsEndOfNavigation',
    description: 'TTS text for navigation command cannot be executed',
  },
};

const addRow = (rows, newRow, newRowPos) => {
  if (newRowPos < 0 || !newRow) return rows;

  let newRows = [];
  if (rows?.length > 0) {
    newRows = [...rows.slice(0, newRowPos), newRow, ...rows.slice(newRowPos)];
  }

  return newRows;
};

const HomeRows = ({
  focusId: focusIdProp,
  onTileClick,
  renderAfterLast,
  onHomeRowsFocusIndexChanged,
  rowsRef,
}) => {
  const [focusId, setFocusId] = useState(focusIdProp);

  useEffect(() => {
    if (focusIdProp) {
      setFocusId(focusIdProp);
    } else {
      setFocusId(focusManager.getNewUniqueId());
    }
  }, [focusIdProp]);

  const data = useSelector(state => state.kitchenSink.data);
  const leagueData = useSelector(state => state.kitchenSink.data.leagues);
  const sportsData = useSelector(state => state.kitchenSink.data.sports);
  const localMarketsData = useSelector(
    state => state.kitchenSink.data.localMarkets,
  );
  const showsData = useSelector(state => state.kitchenSink.data.shows);

  const leagueRow = data?.order?.filter(
    item => item.slug === LEAGUE_ROW_SLUG,
  )[0];

  const sportsRow = data?.order?.filter(
    item => item.slug === SPORTS_ROW_SLUG,
  )[0];

  const localMarketsRow = data?.order?.filter(
    item => item.slug === LOCAL_MARKETS_ROW_SLUG,
  )[0];

  const showsRow = data?.order?.filter(item => item.slug === SHOWS_ROW_SLUG)[0];

  const leagueRowPos = data?.order?.findIndex(
    item => item.slug === LEAGUE_ROW_SLUG,
  );
  const sportsRowPos = data?.order?.findIndex(
    item => item.slug === SPORTS_ROW_SLUG,
  );
  const localMarketsRowPos = data?.order?.findIndex(
    item => item.slug === LOCAL_MARKETS_ROW_SLUG,
  );
  const showsRowPos = data?.order?.findIndex(
    item => item.slug === SHOWS_ROW_SLUG,
  );

  let rows = data?.rows || [];

  if (brand === 'cbs-sports') {
    const addLeagueRow = {
      description: 'leagues row',
      id: 'leagues',
      layoutType: leagueRow?.layout,
      slug: leagueRow?.slug,
      type: leagueRow?.type,
      title: leagueRow?.title,
      url: leagueRow?.url ? leagueRow?.url : '',
      data: leagueData,
    };
    const addSportsRow = {
      description: 'sports row',
      id: 'sports',
      layoutType: sportsRow?.layout,
      slug: sportsRow?.slug,
      type: sportsRow?.type,
      title: sportsRow?.title,
      url: sportsRow?.url ? sportsRow?.url : '',
      data: sportsData,
    };
    if (leagueRowPos < sportsRowPos) {
      rows = addRow(rows, addLeagueRow, leagueRowPos);
      rows = addRow(rows, addSportsRow, sportsRowPos);
    } else {
      rows = addRow(rows, addSportsRow, sportsRowPos);
      rows = addRow(rows, addLeagueRow, leagueRowPos);
    }
  } else if (brand === 'cbs-news') {
    const addLocalMarketsRow = {
      description: 'local markets row',
      id: 'localMarkets',
      layoutType: localMarketsRow?.layout,
      slug: localMarketsRow?.slug,
      type: localMarketsRow?.type,
      title: localMarketsRow?.title,
      url: localMarketsRow?.url ? localMarketsRow?.url : '',
      data: localMarketsData,
    };
    const addShowsRow = {
      description: 'shows row',
      id: 'shows',
      layoutType: showsRow?.layout,
      slug: showsRow?.slug,
      type: showsRow?.type,
      title: showsRow?.title,
      url: showsRow?.url ? showsRow?.url : '',
      data: showsData,
    };
    if (localMarketsRowPos < showsRowPos) {
      rows = addRow(rows, addLocalMarketsRow, localMarketsRowPos);
      rows = addRow(rows, addShowsRow, showsRowPos);
    } else {
      rows = addRow(rows, addShowsRow, showsRowPos);
      rows = addRow(rows, addLocalMarketsRow, localMarketsRowPos);
    }
  }

  rows = [...rows];

  const [games, scoringError] = useScoring();

  const renderRow = useCallback(
    (row, rowIndex) => {
      if (!(row.data?.length || row.id === 'settings')) return null;

      const isPromo = isPromoRow(row);

      let focusIdPrefix;
      if (rowIndex === 0) {
        focusIdPrefix = 'firstrow-';
      } else if (rowIndex === rows.length - 1) {
        focusIdPrefix = 'lastrow-';
      }

      let displayScoring = false;

      let updatedRow = null;
      if (row && isRowWithScoring(row) && !scoringError && games) {
        const highlightsRowData = getHighlightsRowData(row, games);
        if (highlightsRowData.length > 0) {
          displayScoring = true;
          updatedRow = { ...row, data: highlightsRowData };
        }
      }
      updatedRow = updatedRow || row;

      const getHomeRow = () => {
        if (row.id === 'settings') {
          return (
            <SettingsRow className={styles.row} focusIdPrefix={focusIdPrefix} />
          );
        }

        if (row.id === 'leagues') {
          return (
            <RectangleRow
              className={styles.row}
              focusIdPrefix={focusIdPrefix}
              displayTitle
              title="Leagues"
              row={row}
              titleClassName={styles.rectangleRowTitle}
              rowClassName={classNames(styles.row, styles.inverseMargin)}
              rowPos={rowIndex + 1}
              onEndOfNavigationOverride={onEndOfNavigationOverride}
            />
          );
        }
        if (row.id === 'sports') {
          return (
            <CircleRow
              className={styles.row}
              focusIdPrefix={focusIdPrefix}
              displayTitle
              title="Sports"
              row={row}
              titleClassName={styles.circleRowTitle}
              rowClassName={classNames(styles.row, styles.inverseMargin)}
              rowPos={rowIndex + 1}
              onEndOfNavigationOverride={onEndOfNavigationOverride}
            />
          );
        }
        if (row.id === 'localMarkets') {
          return (
            <CircleRow
              className={styles.row}
              focusIdPrefix={focusIdPrefix}
              displayTitle
              title="Local News"
              row={row}
              titleClassName={styles.circleRowTitle}
              rowClassName={classNames(styles.row, styles.inverseMargin)}
              rowPos={rowIndex + 1}
              onEndOfNavigationOverride={onEndOfNavigationOverride}
            />
          );
        }
        if (row.id === 'shows') {
          return (
            <CircleRow
              className={styles.row}
              focusIdPrefix={focusIdPrefix}
              displayTitle
              title="Shows"
              row={row}
              titleClassName={styles.circleRowTitle}
              rowClassName={classNames(styles.row, styles.inverseMargin)}
              rowPos={rowIndex + 1}
              onEndOfNavigationOverride={onEndOfNavigationOverride}
            />
          );
        }

        return (
          <HomeRow
            row={updatedRow}
            displayTitle
            displayScoring={displayScoring}
            games={games}
            onTileClick={onTileClick}
            key={updatedRow.id}
            rowClassName={classNames(styles.row, styles.inverseMargin, {
              [styles.promoRow]: isPromo,
            })}
            titleClassName={classNames(styles.rowTitle, {
              [styles.highlightsRowTitle]: displayScoring,
            })}
            tileClassName={styles.tile}
            focusIdPrefix={focusIdPrefix}
            rowPos={rowIndex + 1}
            onEndOfNavigationOverride={onEndOfNavigationOverride}
          />
        );
      };

      if (rowIndex === 0) {
        return (
          <div>
            {getHomeRow()}
            {rows.length === 1 && renderAfterLast()}
          </div>
        );
      }

      if (rowIndex === rows.length - 1) {
        return (
          <React.Fragment>
            {getHomeRow()}
            {renderAfterLast()}
          </React.Fragment>
        );
      }
      return getHomeRow();
    },
    [onTileClick, games, renderAfterLast, rows.length, scoringError],
  );

  const shouldSnapRowToTop = useCallback(() => true, []);

  const ttsEndOfNavigation = useIntl().formatMessage(
    messages.ttsEndOfNavigation,
  );

  const onEndOfNavigationOverride = index => {
    if (index === 0) {
      focusManager.setCurrentFocus('left-nav-focusable-content');
    }
  };

  return (
    <HomeListRows
      ref={rowsRef}
      focusId={focusId}
      className={styles.listRows}
      data={rows || []}
      renderItem={renderRow}
      buffer={2}
      restorationId={`${focusId}`}
      onListRowsFocusIndexChanged={onHomeRowsFocusIndexChanged}
      shouldSnapRowToTop={shouldSnapRowToTop}
      ttsEndOfNavigation={ttsEndOfNavigation}
    />
  );
};

HomeRows.propTypes = {
  onHomeRowsFocusIndexChanged: PropTypes.func,
  focusId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onTileClick: PropTypes.func,
  renderAfterLast: PropTypes.func,

  rowsRef: Ref,
};

HomeRows.defaultProps = {
  onHomeRowsFocusIndexChanged: () => {},
  focusId: null,
  onTileClick: () => {},
  renderAfterLast: () => null,

  rowsRef: null,
};

export default React.forwardRef((props, ref) => (
  <HomeRows {...props} rowsRef={ref} />
));
